import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { Pagination } from "@mui/material";
import sharedStyles from "../../styles/SharedLayout.module.css";
import styles from "./SearchResults.module.css";
import Filters from "../../components/Filters/Filters";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import {
  searchProducts,
  getAllProductsForSearchFilters,
  type SortOption,
} from "../../utils/meilisearchClient";
import { extractFiltersFromProducts } from "../../utils/filterUtils";
import type { MeilisearchProduct } from "../../types-var/product";
import SearchResultsSkeleton from "./components/SearchResultsSkeleton/SearchResultsSkeleton";
import SearchSEO from "../../components/SEO/SearchSEO"; // Import the new SearchSEO component
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WhatsAppAnnouncementCard from "../../components/WhatsAppAnnouncementCard";
import metaPixelCatalogService from "../../services/metaPixelCatalogService";
import {
  decodeFiltersFromURL,
  updateURLWithFilters,
} from "../../utils/urlFilters";

const SearchResults: React.FC = () => {
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<MeilisearchProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableFilters, setAvailableFilters] = useState<{
    [key: string]: string[];
  }>({});
  const [filterStyles, setFilterStyles] = useState<{ [key: string]: string }>(
    {}
  );
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [totalHits, setTotalHits] = useState(0);
  const [filterCounts, setFilterCounts] = useState<{
    [key: string]: { [value: string]: number };
  }>({});

  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();

  const query = searchParams.get("q") || "";
  const currentSort = (searchParams.get("sort") ||
    "popularity:desc") as SortOption;
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const HITS_PER_PAGE = 24;

  const [localPage, setLocalPage] = useState(currentPage);

  // Track previous values to detect actual changes
  const prevQuery = useRef(query);
  const prevSort = useRef(currentSort);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);

  // Initialize filters from URL parameters on component mount and query changes
  useEffect(() => {
    const filtersFromURL = decodeFiltersFromURL(searchParams);
    setFilters(filtersFromURL);
    // Clear available filters when query changes - they'll be refreshed from new results
    setAvailableFilters({});
  }, [query, searchParams]);

  // Only reset page to 1 when query or sort actually CHANGES (not on initial load)
  useEffect(() => {
    // Skip the reset logic on initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      prevQuery.current = query;
      prevSort.current = currentSort;
      return;
    }

    const queryChanged = prevQuery.current !== query;
    const sortChanged = prevSort.current !== currentSort;

    if (queryChanged || sortChanged) {
      const url = new URL(window.location.href);
      url.searchParams.set("page", "1");
      window.history.replaceState({}, "", url.toString());
      setSearchParams(new URLSearchParams(url.search));
      setLocalPage(1);
    }

    // Update refs with current values
    prevQuery.current = query;
    prevSort.current = currentSort;
  }, [currentSort, query, setSearchParams]);

  const fetchResults = useCallback(
    async (currentFilters: typeof filters) => {
      try {
        const searchOptions: any = {
          sort: [currentSort],
          page: localPage,
          hitsPerPage: HITS_PER_PAGE,
        };

        console.log(`Fetching search results for page: ${localPage}`);

        let filterConditions: string[] = [];

        if (Object.keys(currentFilters).length > 0) {
          Object.entries(currentFilters)
            .filter(([_, values]) => values.length > 0)
            .forEach(([key, values]) => {
              // Use case-insensitive comparison for category filters
              if (key.toLowerCase() === "categorías") {
                const categoryFilters = values.map((categoryValue) => {
                  try {
                    // Parse the JSON string to extract the actual category ID
                    const categoryData = JSON.parse(categoryValue);
                    console.log("🔍 Parsed category data:", categoryData);
                    return `categories.id = ${categoryData.id}`;
                  } catch (error) {
                    // Fallback: if it's not JSON, treat as direct ID
                    console.log("🔍 Using direct category ID:", categoryValue);
                    return `categories.id = ${categoryValue}`;
                  }
                });
                if (categoryFilters.length > 0) {
                  filterConditions.push(`(${categoryFilters.join(" OR ")})`);
                }
              } else {
                const attributeFilters = values.map((value) => {
                  const cleanValue = cleanFilterValue(key, value);
                  try {
                    const parsed = JSON.parse(cleanValue);
                    return `(attributes.name = '${key}' AND attributes.values.name = '${parsed.name}')`;
                  } catch {
                    // Use single quotes to avoid quote conflicts in MeiliSearch filter expressions
                    return `(attributes.name = '${key}' AND attributes.values.name = '${cleanValue}')`;
                  }
                });
                if (attributeFilters.length > 0) {
                  filterConditions.push(`(${attributeFilters.join(" OR ")})`);
                }
              }
            });

          if (filterConditions.length > 0) {
            searchOptions.filter = filterConditions.join(" AND ");
          }
        }

        const results = await searchProducts(query, searchOptions);
        return results;
      } catch (error) {
        console.error("Search failed:", error);
        throw error;
      }
    },
    [currentSort, localPage, query]
  );

  // Utility function to clean filter values by removing attribute name prefix
  const cleanFilterValue = (attributeName: string, filterValue: string) => {
    // Skip cleaning for special filters
    if (attributeName === "Categorías") return filterValue;

    try {
      // Handle JSON values (like colors)
      const parsed = JSON.parse(filterValue);
      if (parsed.name) {
        // Remove attribute name prefix if it exists
        const cleanName = parsed.name.startsWith(attributeName + " ")
          ? parsed.name.substring(attributeName.length + 1)
          : parsed.name;
        return JSON.stringify({ ...parsed, name: cleanName });
      }
      return filterValue;
    } catch {
      // Handle plain string values
      if (filterValue.startsWith(attributeName + " ")) {
        return filterValue.substring(attributeName.length + 1);
      }
      return filterValue;
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    // Update URL with new filters - this will reset page to 1
    updateURLWithFilters(newFilters, searchParams, setSearchParams);
  };

  useEffect(() => {
    const updateResults = async () => {
      setLoading(true);
      try {
        const results = await fetchResults(filters);

        // Fetch ALL products for filter extraction with current filters applied
        const filterConditions: string[] = [];

        Object.entries(filters).forEach(([filterType, values]) => {
          if (values.length > 0) {
            // Use case-insensitive comparison for category filters
            if (filterType.toLowerCase() === "categorías") {
              const categoryFilters = values.map((categoryValue) => {
                try {
                  // Parse the JSON string to extract the actual category ID
                  const categoryData = JSON.parse(categoryValue);
                  console.log(
                    "🔍 updateResults - Parsed category data:",
                    categoryData
                  );
                  return `categories.id = ${categoryData.id}`;
                } catch (error) {
                  // Fallback: if it's not JSON, treat as direct ID
                  console.log(
                    "🔍 updateResults - Using direct category ID:",
                    categoryValue
                  );
                  return `categories.id = ${categoryValue}`;
                }
              });
              if (categoryFilters.length > 0) {
                filterConditions.push(`(${categoryFilters.join(" OR ")})`);
              }
            } else {
              const attributeFilters = values.map((value) => {
                const cleanValue = cleanFilterValue(filterType, value);
                try {
                  const parsed = JSON.parse(cleanValue);
                  return `(attributes.name = '${filterType}' AND attributes.values.name = '${parsed.name}')`;
                } catch {
                  return `(attributes.name = '${filterType}' AND attributes.values.name = '${cleanValue}')`;
                }
              });
              if (attributeFilters.length > 0) {
                filterConditions.push(`(${attributeFilters.join(" OR ")})`);
              }
            }
          }
        });

        const allProductsForFilters = await getAllProductsForSearchFilters(
          query,
          filters // Pass the original filters object instead of converting to string
        );

        const {
          filters: extractedFilters,
          styles,
          counts,
        } = extractFiltersFromProducts(allProductsForFilters);

        console.log("SearchResults Debug:", {
          query,
          extractedFilterKeys: Object.keys(extractedFilters),
          hasCategorias: !!extractedFilters["Categorías"],
          categoriasCount: extractedFilters["Categorías"]?.length || 0,
          allProductsCount: allProductsForFilters.length,
          sampleProduct: allProductsForFilters[0]?.categories?.length
            ? {
                productName: allProductsForFilters[0].name,
                categoriesCount: allProductsForFilters[0].categories.length,
                firstCategory: allProductsForFilters[0].categories[0],
              }
            : "No categories found",
        });

        setProducts(results.hits as MeilisearchProduct[]);
        setAvailableFilters(extractedFilters);
        setFilterStyles(styles);
        setFilterCounts(counts);
        setTotalPages(results.totalPages ?? 1);
        setTotalHits(results.totalHits ?? 0);

        // Validate if current page exists with applied filters
        const totalPagesValue = results.totalPages ?? 1;
        if (localPage > totalPagesValue && totalPagesValue > 0) {
          console.log(
            `Page ${localPage} doesn't exist with current filters (max: ${totalPagesValue}). Redirecting to page 1.`
          );

          // Update URL to page 1 while keeping all other parameters
          const url = new URL(window.location.href);
          url.searchParams.set("page", "1");
          window.history.replaceState({}, "", url.toString());
          setLocalPage(1);
          return; // Exit early to prevent further processing
        }

        // Track search with enhanced Meta Pixel catalog data
        if (results.hits.length > 0 && query) {
          metaPixelCatalogService.trackSearch(
            query,
            results.hits.map((product: any) => ({
              id: product.id,
              name: product.name,
              price:
                product.price?.discount?.newPrice ||
                product.price?.regPrice ||
                0,
              discountPrice: product.price?.discount?.newPrice,
              category: product.categories?.[0]?.name || "Unknown",
              brand: product.partner?.name || "Unknown",
              sku: product.sku || "",
              stock: product.stock || 0,
              images: product.images || [],
              customizableOptions: product.customizableOptions || [],
              attributes: product.attributes || [],
            })),
            "product"
          );
        }
      } catch (error) {
        setError("Failed to load search results. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    updateResults();
  }, [query, currentSort, localPage, filters, fetchResults]);

  const handleSortChange = (sortBy: SortOption) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("sort", sortBy);
    newSearchParams.set("page", "1");
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (_event) {
      _event.preventDefault();
    }

    setLocalPage(page);

    console.log(`Setting page to ${page}, updating URL and fetching new data`);

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    window.history.pushState({}, "", url.toString());
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    console.log(`Current page state changed to: ${currentPage}`);
  }, [currentPage]);

  if (loading) {
    return <SearchResultsSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!loading && products.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateTitle}>Sin resultados para "{query}"</p>
        <p className={styles.emptyStateMessage}>
          Intenta con diferentes palabras clave o elimina algunos filtros
        </p>

        <div className={styles.whatsappContainer}>
          <div className={styles.whatsappContent}>
            <WhatsAppIcon className={styles.whatsappIcon} />
            <div className={styles.whatsappText}>
              <h3>¿No encuentras lo que buscas?</h3>
              <p>
                ¡Podemos ayudarte! Pregúntanos directamente por WhatsApp y
                haremos lo posible por conseguir el producto que necesitas.
              </p>
            </div>
          </div>
          <a
            href="https://wa.me/51906869576?text=Hola,%20estoy%20buscando%20un%20producto%20que%20no%20encuentro%20en%20la%20web:%20"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.whatsappButton}
          >
            Contactar por WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add SearchSEO component */}
      <SearchSEO query={query} totalResults={totalHits} />

      <div className="container">
        <div className={sharedStyles.pageContainer}>
          <h1 className={sharedStyles.pageTitle}>Resultados para "{query}"</h1>

          <div className={sharedStyles.mainContent}>
            <div className={sharedStyles.filtersColumn}>
              {/* Only show Filters in desktop view */}
              <div className={styles.desktopFiltersOnly}>
                <Filters
                  availableFilters={availableFilters}
                  filters={filters}
                  filterStyles={filterStyles}
                  setFilters={handleFilterChange}
                  filterCounts={filterCounts}
                  isMobile={false}
                />
              </div>
            </div>

            <div className={sharedStyles.productsColumn}>
              <ProductGrid
                products={products}
                toggleMiniCart={toggleMiniCart}
                onSortChange={handleSortChange}
                currentSort={currentSort}
                filterStyles={filterStyles}
                availableFilters={availableFilters}
                filterCounts={filterCounts}
                totalHits={totalHits}
                query={query}
                filters={filters}
                setFilters={handleFilterChange}
                onClearFilters={() => handleFilterChange({})}
              />
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <Pagination
                    count={totalPages}
                    page={localPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    className="customPagination"
                    showFirstButton={totalPages > 5}
                    showLastButton={totalPages > 5}
                    siblingCount={0}
                    boundaryCount={1}
                    hidePrevButton={false}
                    hideNextButton={false}
                  />
                  <div className={styles.paginationInfo}>
                    Pág {localPage} de {totalPages}
                  </div>
                </div>
              )}

              {/* WhatsApp Banner */}
              <WhatsAppAnnouncementCard />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResults;
